/* eslint-disable import/no-mutable-exports */
import { replayCanvasIntegration, replayIntegration, init } from "@sentry/react";
import { AxiosInstance } from "axios";
import { isDynamicImportErrorString } from "./utils/dynamic-import-error";
import { getEnvironment } from "./environment/environment-helpers";
import { getReplaySessionSampleRate, getSentryDsn, getInstance } from "./instance/instance-config";
import { ObservabilityAndMonitoringConfig } from "./initialise-observability-and-monitoring-types";

declare const VERSION: string;

export let sessionReplayIntegration: ReturnType<typeof replayIntegration> | null = null;

export let canvasReplayIntegration: ReturnType<typeof replayCanvasIntegration> | null = null;

export let usageMetricsEnabled = false;

export let usageApi: AxiosInstance | null = null;

export function initialiseObservabilityAndMonitoring(config: ObservabilityAndMonitoringConfig, appUrl: string) {
    const integrations = [];

    const hostName = window.location.host;
    const regexHostName = hostName.replace(/\./g, "\\.");

    if (config.replayEnabled) {
        sessionReplayIntegration = replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
            block: ["[type='password']"],
            networkDetailAllowUrls: [`https://${hostName}/api/`],
            networkDetailDenyUrls: [
                // Filter out anything related to user authentication
                `https://${hostName}/api/permissions/authentication/`,
                // Filter out any requests made to the assets service
                `https://${hostName}/api/assets/`,
                // Filter out any map object tile requests
                new RegExp(`https://${regexHostName}/api/mapping/map/[^/]+/tiles/.*`),
                // Filter out any map object comment tile requests
                new RegExp(`https://${regexHostName}/api/mapping/maps/[^/]+/comments/tiles/.*`),
            ],
        });
        canvasReplayIntegration = replayCanvasIntegration();

        integrations.push(sessionReplayIntegration, canvasReplayIntegration);
    }

    if (config.errorTrackingEnabled) {
        const instance = getInstance(hostName);
        init({
            dsn: getSentryDsn(instance),
            integrations,
            replaysSessionSampleRate: getReplaySessionSampleRate(instance),
            // Capture all sessions which have an error
            replaysOnErrorSampleRate: 1.0,
            environment: getEnvironment(appUrl),
            release: VERSION,
            enableLogs: true,
            beforeSend: (event) => {
                // Exclude "failed to fetch dynamically imported module" error, as these are caused by app updates or bad internet connections
                if (event.exception?.values?.some((exception) => exception?.value && isDynamicImportErrorString(exception.value))) {
                    return null;
                }
                return event;
            },
        });
    }

    usageMetricsEnabled = config.usageMetricsEnabled;
}

export function setUsageApi(_usageApi: AxiosInstance) {
    usageApi = _usageApi;
}
